import React, { useCallback, useMemo, useState } from 'react';
import { mobileConfig } from '../../commons/config';
import useAnalytics from '../../web-analytics/webAnalyticsContext';
import { analyticsTrack } from '../../web-analytics/webAnalytics';
import { AnalyticsEvent } from '../../web-analytics/AnalyticsEvent';
import { useQueryCacheManager } from '../../hooks/useQueryCacheManager';
import { useMutation } from '@tanstack/react-query';
import useApi from '../../api/backendApiContext';
import { ConfirmDialogProps, ConfirmModal } from '../../commons/components/ConfirmModal';
import { useNotification } from '../../notification/notificationsContext';
import { errorToString } from '../../util/utils';
import instagramImage from '../../assets/images/instagram.png';
import { IntegrationTile } from '../../commons/components/IntegrationTile';
import { notConnectedInstagramAccount } from '../../social-posting/socialPostingApi';
import { useFetchedInstagramAccount } from '../hooks/useFetchedInstagramAccount';
import { useConnectInstagram } from '../hooks/useConnectInstagram';
import { ExternalBrowserDialog } from '../../commons/components/ExternalBrowserDialog';

export const InstagramSettings = () => {
  const analytics = useAnalytics();

  const {
    query: { isLoading, isError, data },
    queryStatusComponent,
  } = useFetchedInstagramAccount();
  const { socialPostingApi } = useApi();
  const { notifySuccess, notifyError } = useNotification();

  const isActive = !!data && !data.isDisconnected;
  const isDisconnected = !!data && data.isDisconnected;

  const queryCacheManager = useQueryCacheManager();

  const [openBrowserKey, setOpenBrowserKey] = useState<string | null>(null);
  const { startConnect, isConnecting } = useConnectInstagram();
  const onConnectClick = useCallback(() => {
    if (mobileConfig().isMobile) {
      setOpenBrowserKey(new Date().toString());
      return;
    }
    startConnect();
  }, [startConnect]);

  const { isLoading: isDisconnecting, mutate: disconnect } = useMutation(
    async () => socialPostingApi.disconnectInstagram(),
    {
      onSuccess: () => {
        queryCacheManager.setInstagramAccount(notConnectedInstagramAccount);
        const message = 'Instagram has been successfully disconnected.';
        notifySuccess({
          notificationMsg: message,
          logMsg: message,
        });
        analyticsTrack(analytics, AnalyticsEvent.INSTAGRAM_DISCONNECTED);
      },
      onError: (e) => {
        const message = errorToString(
          e,
          'Something went wrong while disconnecting from Instagram. Please try again.'
        );
        notifyError({
          notificationMsg: message,
          logMsg: message,
        });
      },
    }
  );

  const [confirmDisconnectModal, setConfirmDisconnectModal] = useState(false);

  const onDisconnectConfirmed = useCallback(() => {
    setConfirmDisconnectModal(false);
    disconnect();
  }, [disconnect]);

  const disconnectConfirmDialogProps: ConfirmDialogProps = useMemo<ConfirmDialogProps>(
    () => ({
      title: 'Disconnect Growegy and Instagram?',
      message: "You'll be able to reconnect at any time.",
      footer: {
        type: 'show',
        applyButton: {
          label: 'Disconnect',
          variant: 'danger',
          onClick: onDisconnectConfirmed,
        },
        cancelButton: {
          label: 'Cancel',
          onClick: () => setConfirmDisconnectModal(false),
        },
      },
    }),
    [onDisconnectConfirmed]
  );

  return (
    <>
      <IntegrationTile
        isActive={isActive}
        isDisconnected={isDisconnected}
        isConnecting={isConnecting}
        isDisconnecting={isDisconnecting}
        isSchedulingResync={false}
        isLoading={isLoading}
        isError={isError}
        queryStatusComponent={queryStatusComponent}
        title="Instagram Business"
        subtitle={data ? (data.username ? `@${data.username}` : data.name) : null}
        privacyPolicyUrl="https://www.growegy.com/resources/about/privacy-policy/"
        learnMoreViewedEvent={AnalyticsEvent.INSTAGRAM_HELP_VIEWED}
        onConnectClick={onConnectClick}
        onDisconnectClick={() => setConfirmDisconnectModal(true)}
        icon={<img className="integration-row-icon" src={instagramImage} alt="Instagram" />}
      >
        <div>Connect your Instagram Business account directly to create and schedule posts.</div>
      </IntegrationTile>
      {confirmDisconnectModal && <ConfirmModal {...disconnectConfirmDialogProps} />}
      <ExternalBrowserDialog dialogKey={openBrowserKey} title="Connect to Instagram">
        To integrate your Instagram account with Growegy, you'll be redirected to an external
        browser to securely log in and complete the connection. Tap Proceed to continue.
      </ExternalBrowserDialog>
    </>
  );
};
